import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  DescriptionList,
  FormField,
  Notice,
  Radio,
  Select,
  Spinner,
  Text,
  Textarea,
} from '@heartinternet/hi-ui';
import type { AssignableHostingPackage, DecodedCsr, OrgFormData } from '../types';
import { endpoints, isValidCsr, rpcPost, validateSubdomain } from '../helpers';

/** The slice of install state this section owns. */
export interface DomainValue {
  domainName: string | null;
  domainOid: string | null;
  subdomain: string;
  csr: string | null;
}

export interface DomainSectionProps {
  packages: AssignableHostingPackage[];
  isWildcard: boolean;
  value: DomainValue;
  onChange: (patch: Partial<DomainValue>) => void;
  onValidChange: (valid: boolean) => void;
  /** Prefill the Organisation Details form from a decoded CSR (OV/EV). */
  onCsrOrgData: (patch: Partial<OrgFormData>) => void;
  baseUrl: string;
  fetchFn: typeof window.fetch;
  invalid: boolean;
}

type Mode = 'search' | 'csr';

/** Map decoded CSR subject → org form fields (address is intentionally not mapped — see plan). */
function decodedToOrg(d: DecodedCsr): Partial<OrgFormData> {
  return {
    organisation: d.organisation,
    city: d.locality,
    state: d.state,
    country: d.country,
    postcode: d.postcode,
    email: d.email,
  };
}

/** Assign Domain card — mirrors submission/section-domain.js. */
export const DomainSection: React.FC<DomainSectionProps> = ({
  packages,
  isWildcard,
  value,
  onChange,
  onValidChange,
  onCsrOrgData,
  baseUrl,
  fetchFn,
  invalid,
}) => {
  const hasPackages = packages.length > 0;
  const [mode, setMode] = useState<Mode>(hasPackages ? 'search' : 'csr');
  const [subdomainChecked, setSubdomainChecked] = useState(false);
  const [subdomainInput, setSubdomainInput] = useState('');
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  const [csrText, setCsrText] = useState('');
  const [csrError, setCsrError] = useState<string | null>(null);
  const [csrWarning, setCsrWarning] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState<DecodedCsr | null>(null);
  const lastDecodedRef = useRef<string | null>(null);

  const sortedPackages = useMemo(
    () => [...packages].sort((a, b) => a.name.localeCompare(b.name)),
    [packages]
  );

  // Report validity.
  useEffect(() => {
    const valid =
      mode === 'search'
        ? value.domainName !== null && !(subdomainChecked && subdomainError !== null)
        : value.csr !== null &&
          value.domainName !== null &&
          csrError === null &&
          csrWarning === null;
    onValidChange(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, value.domainName, value.csr, subdomainChecked, subdomainError, csrError, csrWarning]);

  const resetForMode = (next: Mode) => {
    setMode(next);
    setSubdomainChecked(false);
    setSubdomainInput('');
    setSubdomainError(null);
    setCsrText('');
    setCsrError(null);
    setCsrWarning(null);
    setDecoded(null);
    lastDecodedRef.current = null;
    onChange({
      domainName: null,
      domainOid: null,
      subdomain: isWildcard ? '*' : '',
      csr: next === 'csr' ? '' : null,
    });
  };

  // Bind the <select> by list index, not package OID: every domain on a package is offered under
  // that package's OID (it's the install_to target), so several domains share one OID. A controlled
  // select whose value is that shared OID snaps back to the first option carrying it — reverting the
  // user's pick and issuing the certificate for the wrong common name (issue #290). The index is
  // unique per row; the chosen entry still supplies its own name (→ common name) and OID (→ install_to).
  const selectedIndex = sortedPackages.findIndex(
    (p) => p.name === value.domainName && p.oid === value.domainOid
  );

  const handlePackageSelect = (index: string) => {
    // index is '' (placeholder) or a real option index — never out of range.
    const pkg = index === '' ? null : sortedPackages[Number(index)];
    onChange({ domainName: pkg?.name ?? null, domainOid: pkg?.oid ?? null });
  };

  const handleSubdomainToggle = (checked: boolean) => {
    setSubdomainChecked(checked);
    if (!checked) {
      setSubdomainInput('');
      setSubdomainError(null);
      onChange({ subdomain: '' });
    }
  };

  const handleSubdomainInput = (raw: string) => {
    const v = raw.toLowerCase();
    setSubdomainInput(raw);
    const { valid, error } = validateSubdomain(v);
    setSubdomainError(error);
    onChange({ subdomain: valid ? v : '' });
  };

  const decodeCsr = async (text: string) => {
    const trimmed = text.trim();
    /* v8 ignore next -- redundant-decode guard; only hit by re-submitting an identical CSR */
    if (trimmed === lastDecodedRef.current) return;
    lastDecodedRef.current = trimmed;
    setDecoding(true);
    setCsrError(null);
    setCsrWarning(null);
    const res = await rpcPost<DecodedCsr>(fetchFn, endpoints.decodecsr(baseUrl), { csr: trimmed });
    setDecoding(false);
    if (res.error || !res.data) {
      setCsrWarning(res.error ?? 'We could not decode that CSR.');
      setDecoded(null);
      onChange({ domainName: null, csr: null });
      return;
    }
    const data = res.data;
    if (isWildcard && !(data.common_name ?? '').includes('*.')) {
      setCsrWarning('The common name must start with a wildcard (*.)');
      setDecoded(null);
      onChange({ domainName: null, csr: null });
      return;
    }
    setDecoded(data);
    onChange({ domainName: data.common_name, domainOid: null, csr: trimmed });
    onCsrOrgData(decodedToOrg(data));
  };

  const handleCsrChange = (text: string) => {
    setCsrText(text);
    if (text.trim() === '') {
      setCsrError(null);
      setCsrWarning(null);
      setDecoded(null);
      lastDecodedRef.current = null;
      onChange({ domainName: null, csr: '' });
      return;
    }
    if (!isValidCsr(text)) {
      setCsrError('Please include the full CSR, including the BEGIN and END lines');
      setDecoded(null);
      onChange({ domainName: null, csr: null });
      return;
    }
    setCsrError(null);
    void decodeCsr(text);
  };

  const cardClass = ['hi-ui-ssl-info__section', invalid && 'hi-ui-ssl-info__section--error']
    .filter(Boolean)
    .join(' ');

  // Coverage preview lines for the selected package.
  const previewLines: string[] = [];
  if (mode === 'search' && value.domainName) {
    if (isWildcard) {
      previewLines.push(value.domainName, `*.${value.domainName}`);
    } else if (subdomainChecked && value.subdomain) {
      previewLines.push(`${value.subdomain}.${value.domainName}`);
    } else {
      previewLines.push(value.domainName, `www.${value.domainName}`);
    }
  }

  const decodedItems = decoded
    ? Object.entries({
        'Common name': decoded.common_name,
        Organisation: decoded.organisation,
        Locality: decoded.locality,
        State: decoded.state,
        Country: decoded.country,
        Postcode: decoded.postcode,
        Email: decoded.email,
      })
        .filter(([, v]) => v)
        .map(([label, v]) => ({ label, value: v as string }))
    : [];

  return (
    <Card
      className={cardClass}
      variant="surface"
      header={<Text variant="heading-3">Assign domain</Text>}
    >
      <div className="hi-ui-ssl-info__section-body">
        <Text variant="body">Please assign a domain to your SSL certificate.</Text>

        {hasPackages && (
          <div className="hi-ui-ssl-info__domain-option">
            <Radio
              name="domain-choice"
              value="search"
              checked={mode === 'search'}
              onChange={() => resetForMode('search')}
              label="Select one of your available hosting packages"
            />
            {mode === 'search' && (
              <div className="hi-ui-ssl-info__domain-search">
                <Text variant="body-small">
                  {isWildcard
                    ? 'Choose your hosting package. Your Wildcard SSL will cover it and all subdomains (shown as a *).'
                    : 'Choose your hosting package. Your SSL will cover it and also the www subdomain.'}
                </Text>
                <FormField label="Hosting package" fullWidth>
                  <Select
                    name="package"
                    value={selectedIndex >= 0 ? String(selectedIndex) : ''}
                    fullWidth
                    onChange={(e) => handlePackageSelect(e.target.value)}
                  >
                    <option value="">Select a hosting package</option>
                    {sortedPackages.map((p, i) => (
                      <option key={`${p.oid}-${p.name}`} value={i}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </FormField>

                {previewLines.length > 0 && (
                  <div className="hi-ui-ssl-info__preview">
                    <Text variant="label">Protects:</Text>
                    {previewLines.map((line) => (
                      <Text key={line} variant="body-small">
                        {line}
                      </Text>
                    ))}
                  </div>
                )}

                {!isWildcard && (
                  <div className="hi-ui-ssl-info__subdomain">
                    <label className="hi-ui-ssl-info__checkbox-row">
                      <input
                        type="checkbox"
                        checked={subdomainChecked}
                        disabled={!value.domainName}
                        onChange={(e) => handleSubdomainToggle(e.target.checked)}
                      />
                      <Text variant="inline">Protect a subdomain</Text>
                    </label>
                    {subdomainChecked && (
                      <>
                        <Text variant="body-small">
                          Instead, set your SSL to protect a specific subdomain. Include it below by
                          using letters, numbers, full stops and hyphens only.
                        </Text>
                        <FormField label="Subdomain" error={subdomainError ?? undefined} fullWidth>
                          <input
                            className="hi-ui-input"
                            type="text"
                            placeholder="Add a subdomain"
                            value={subdomainInput}
                            onChange={(e) => handleSubdomainInput(e.target.value)}
                          />
                        </FormField>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="hi-ui-ssl-info__domain-option">
          {hasPackages && (
            <Radio
              name="domain-choice"
              value="csr"
              checked={mode === 'csr'}
              onChange={() => resetForMode('csr')}
              label="Add a CSR"
            />
          )}
          {mode === 'csr' && (
            <div className="hi-ui-ssl-info__domain-csr">
              <Text variant="body-small">
                Generate your own CSR and Private Key for a domain you own and paste the CSR below.
                Remember to save your Private Key somewhere safe. Please do not use non-ASCII
                characters when generating your CSR.
              </Text>
              {hasPackages && (
                <Text variant="body-small">
                  Choose this option for domains that are not linked to one of your hosting
                  packages.
                </Text>
              )}
              <Text variant="body-small">
                {isWildcard
                  ? 'For wildcard SSLs please include the *. within the common name field.'
                  : 'If you want your SSL to protect a subdomain, include this in the common name field.'}
              </Text>
              <FormField label="CSR" error={csrError ?? undefined} fullWidth>
                <Textarea
                  name="csr"
                  rows={8}
                  value={csrText}
                  fullWidth
                  placeholder={
                    'Paste your whole CSR here, including\n-----BEGIN CERTIFICATE REQUEST-----\nand\n-----END CERTIFICATE REQUEST-----'
                  }
                  onChange={(e) => handleCsrChange(e.target.value)}
                />
              </FormField>
              {decoding && (
                <div className="hi-ui-ssl-info__inline-spinner">
                  <Spinner size="small" aria-label="Decoding CSR" />
                  <Text variant="inline">Loading...</Text>
                </div>
              )}
              {csrWarning && <Notice variant="error">{csrWarning}</Notice>}
              {decodedItems.length > 0 && (
                <DescriptionList layout="horizontal" items={decodedItems} />
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
